<script setup lang="ts">
import type { TableField, TableAction } from "./table";
import IconComponent from "../../components/ui/common/IconComponent.vue";
import { formatFieldValue } from "./tableUtils";
import { Permission } from "../../types/auth";
import { hasPermission, isEmpty } from "../../utils/CommonFunctions";
import AsyncImage from "../ui/form/AsyncImage.vue";
import DropdownButton from "../ui/common/DropDownButton.vue";
import IconButton from "../ui/form/IconButton.vue";
import NumberInput from "../ui/form/NumberInput.vue";

const props = defineProps<{
  items: any[];
  fields: TableField[];
  actions: TableAction[];
  sortConfig: { field: string; direction: "asc" | "desc" } | null;
  allowDisable: boolean;
  showQuickView: boolean;
  permissions?: Permission[];
  showActions: boolean;
  showChildRow?: boolean;
  childRowFields?: TableField[];
  childRowTitle?: string;
  childRowDataField?: string;
  childRowTableWidth?: string;
  tableWidth?: string;
  child?: boolean;
  readonly?: boolean;
  ignoreZero?: boolean;
}>();

defineEmits<{
  (e: "sort", field: string): void;
  (e: "rowDblClick", item: any): void;
  (e: "action", action: string, item: any): void;
}>();

</script>

<template>
  <div class="relative hidden md:block">
    <table
      class="divide-y divide-gray-200 dark:divide-gray-700"
      :class="[
        !isEmpty(tableWidth) ? 'w-[' + tableWidth + 'px]' : 'min-w-full',
      ]"
      id="table-records"
    >
      <thead
        :class="{
          'bg-gray-50 dark:bg-gray-800': !child,
          'bg-gray-45 dark:bg-gray-600': child,
        }"
      >
        <tr id="table-headers">
          <th
            v-for="field in fields.filter((f) => f.hide != true)"
            :key="field.name"
            @click="field.sortable && $emit('sort', field.name)"
            :style="
              field.width ? { width: field.width, minWidth: field.width } : {}
            "
            class="px-6 py-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
            :class="{
              'cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700':
                field.sortable,
              'text-right':
                field.align == 'right' || field.fieldType == 'number',
              'text-left': field.align == 'left' || !field.align,
              'text-center': field.align == 'center',
            }"
          >
            <div class="items-center gap-1" :class="{ flex: field.sortable }">
              {{ field.caption }}
              <span
                v-if="field.sortable"
                class="text-gray-400"
                id="table-header-sort"
              >
                {{
                  sortConfig?.field === field.name
                    ? sortConfig.direction === "asc"
                      ? "↑"
                      : "↓"
                    : "↕"
                }}
              </span>
            </div>
          </th>
          <th
            v-if="actions.length && showActions == true"
            :style="{
              width:
                actions.length == 1 ? '50px' : 30 + actions.length * 20 + 'px',
            }"
            class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky right-0 bg-gray-50 dark:bg-gray-800 shadow-l"
          >
            Actions
          </th>
        </tr>
      </thead>
      <tbody
        class=""
        :class="{
          'bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700':
            !child,
          'bg-white dark:bg-gray-700 divide-y divide-gray-100 dark:divide-gray-300':
            child,
        }"
      >
        <template
          v-if="items && items.length > 0"
          v-for="(item, index) in items"
          :key="item.id"
        >
            <tr
            :class="{
              'hover:bg-gray-100 dark:hover:bg-gray-700': !child,
              'hover:bg-gray-200 dark:hover:bg-gray-600': child,
            }"
            >
            <td
              v-for="(field, fieldIndex) in fields.filter((f) => f.hide != true)"              
              :key="field.name + '-' + index"
              @dblclick="field.fieldType != 'number-input'? $emit('rowDblClick', item): ''"
              class="py-4 whitespace-nowrap"
              :class="{
                'text-right': field.fieldType == 'number',
                'px-6': !showChildRow || fieldIndex != 0,
              }"
            >
              <div :class="{'flex justify-start': showChildRow && fieldIndex == 0, 'pl-[35px]' : showChildRow && fieldIndex == 0 && item[props.childRowDataField ?? ''].length == 0}">
                <IconButton
                  @click="item.expanded = false"
                  icon="CircleArrowDown"
                  class="mr-3"
                  v-if="fieldIndex == 0 && showChildRow && item.expanded == true && item[props.childRowDataField ?? ''].length > 0"
                />
                <IconButton
                  @click="item.expanded = true"
                  icon="CircleArrowRight"
                  class="mr-3"
                  v-if="fieldIndex == 0 && showChildRow && item.expanded != true && item[props.childRowDataField ?? ''].length > 0"
                />
                <div
                  class="flex justify-center"
                  v-if="field.fieldType == 'checkbox'"
                >
                  <input
                    type="checkbox"
                    readonly
                    onclick="return false"
                    :checked="item[field.name]"
                    class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 pointer-non"
                  />
                </div>
                <div
                  class="flex justify-center"
                  v-else-if="field.fieldType == 'number-input'"
                >
                <NumberInput 
                  :readonly="readonly"
                  :show-controls="false"
                  size="md"
                  v-model="item[field.name]"
                  label="" />
                </div>
                <div
                  style="height: 35px; display: block"
                  v-else-if="field.fieldType == 'image'"
                >
                  <AsyncImage
                    :src="item[field.name]"
                    :readonly="false"
                    className="h-12 w-12 rounded-full"
                  />
                </div>
                <div v-else-if="field.fieldType == 'icon'">
                  <IconComponent :name="item[field.name]" />
                </div>
                <div
                  v-else-if="field.fieldType == 'boolean'"
                  class="text-center"
                >
                  {{
                    item[field.name] == "1" || item[field.name] == 1
                      ? "YES"
                      : "NO"
                  }}
                </div>
                <div
                  v-else-if="field.fieldType != 'hidden'"
                  v-html="formatFieldValue(item[field.name], field.fieldType, ignoreZero)"
                ></div>
              </div>
            </td>
            <td
              v-if="actions.length > 0 && showActions == true"
              class="whitespace-nowrap text-right sticky right-0 shadow-l"
              id="table-actions"
            >
              <div class="px-6 py-4 bg-white dark:bg-gray-900 space-x-2">
                <template v-for="action in props.actions">
                  <button
                    :key="action.name"
                    :id="'table-action-' + action.name"
                    @click="$emit('action', action.name, item)"
                    v-if="
                      (hasPermission(props.permissions, action.name) ||
                        hasPermission(props.permissions, action.permission) ||
                        action.ignorePermission) &&
                      !action.dropdown &&
                      (typeof action.visible === 'function'
                        ? action.visible(item)
                        : action.visible)
                    "
                    :class="[
                      'text-' +
                        action.variant +
                        '-600 hover:text-' +
                        action.variant +
                        '-700',
                      'dark:text-' +
                        action.variant +
                        '-400 dark:hover:text-' +
                        action.variant +
                        '-300',
                    ]"
                    :title="action.title"
                  >
                    <IconComponent :name="action.icon" v-if="action.icon" />
                    <font-awesome-icon v-if="action.faIcon" class="mr-1" :icon="action.faIcon"  :width="20" :height="20" />
                  </button>
                </template>
                <button
                  v-if="
                    props.allowDisable == true &&
                    hasPermission(
                      props.permissions,
                      item.active == true ? 'Disable' : 'Enable'
                    )
                  "
                  key="disable-records"
                  id="table-action-toggle-status"
                  @click="$emit('action', 'toggleStatus', item)"
                  :class="[
                    'text-danger-600 hover:text-danger-700',
                    'dark:text-danger-400 dark:hover:text-danger-300',
                  ]"
                  :title="item.active == true ? 'Disable' : 'Enable'"
                >
                  <IconComponent
                    :name="item.active == true ? 'Ban' : 'CircleCheck'"
                  />
                </button>
                <DropdownButton
                  key="action-dropdown"
                  id="actions-dropdown"
                  :hideArrow="true"
                  v-if="
                    actions.filter(
                      (e) =>
                        e.dropdown == true &&
                        (typeof e.visible === 'function'
                          ? e.visible(item)
                          : e.visible) &&
                        (hasPermission(props.permissions, e.name) ||
                          hasPermission(props.permissions, e.permission) ||
                          e.ignorePermission)
                    ).length > 0
                  "
                >
                  <template
                    v-for="action in actions.filter(
                      (e) =>
                        e.dropdown == true &&
                        (typeof e.visible === 'function'
                          ? e.visible(item)
                          : e.visible) &&
                        (hasPermission(props.permissions, e.name) ||
                          hasPermission(props.permissions, e.permission) ||
                          e.ignorePermission)
                    )"
                  >
                    <button
                      @click="$emit('action', action.name, item)"
                      class="block flex gap-2 w-full px-4 py-2 text-sm text-left text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                    >
                      <IconComponent :name="action.icon" />
                      {{ action.title }}
                    </button>
                  </template>
                </DropdownButton>
              </div>
            </td>
          </tr>
          <tr
            v-if="
              showChildRow &&
              props.childRowDataField &&
              item[props.childRowDataField ?? ''].length > 0 &&
              item.expanded == true
            "
          >
            <td :colspan="fields.length + 1" class="pl-5 pb-5 mb-3">
              <h3 class="p-3">{{ props.childRowTitle }}</h3>
              <DataTableDesktop
                :items="item[props.childRowDataField ?? '']"
                :actions="actions"
                :sortConfig="null"
                :showActions="false"
                :allowDisable="false"
                :showQuickView="false"
                :fields="childRowFields || []"
                :tableWidth="childRowTableWidth"
                :childRowDataField="childRowDataField"
                :child="true"
              />
            </td>
          </tr>
        </template>
        <tr v-else>
          <td :colspan="fields.length + 1" class="text-center py-4">
            No records found
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
